import type { AxiosError } from "axios";
import * as ApiService from "@/services/ApiService";
import type {
  ApiBoolResult,
  ApiIdResult,
  FacilityPatientForm,
} from "@/types/portal-forms";

function seg(s: string): string {
  return encodeURIComponent(s);
}

function messageFromAxiosError(err: unknown): string {
  const ax = err as AxiosError<Record<string, unknown>>;
  const d = ax.response?.data;
  if (d && typeof d === "object") {
    for (const key of ["reason", "message", "title", "detail"] as const) {
      const v = d[key];
      if (v != null && String(v).trim()) return String(v).trim();
    }
  }
  const status = ax.response?.status;
  const text = ax.response?.statusText;
  if (status != null && text) return `HTTP ${status}: ${text}`;
  if (status != null) return `HTTP ${status}`;
  return ax.message?.trim() || "Request failed.";
}

export async function getPortalForm(
  facilityKey: string,
  patientKey: string,
  facilityFormId: number
): Promise<FacilityPatientForm> {
  const res = await ApiService.get(
    `/Portal/GetForm/${seg(facilityKey)}/${seg(patientKey)}/${facilityFormId}`
  );
  return res.data as FacilityPatientForm;
}

export async function uploadPortalForm(
  facilityKey: string,
  patientKey: string,
  facilityFormId: number,
  base64: string
): Promise<ApiIdResult> {
  try {
    const res = await ApiService.post(
      `/Portal/UploadForm/${seg(facilityKey)}/${seg(patientKey)}/${facilityFormId}`,
      { base64 }
    );
    return res.data as ApiIdResult;
  } catch (e) {
    throw new Error(`UploadForm: ${messageFromAxiosError(e)}`);
  }
}

export async function updatePortalFormComplete(
  facilityKey: string,
  patientKey: string,
  facilityFormId: number,
  /** JSON string: SurveyJS result data (answers), including signature base64 as in form / `patientSignature`. */
  data: string
): Promise<ApiBoolResult> {
  try {
    const res = await ApiService.post(
      `/Portal/UpdateFormComplete/${seg(facilityKey)}/${seg(patientKey)}/${facilityFormId}`,
      { data }
    );
    return res.data as ApiBoolResult;
  } catch (e) {
    throw new Error(`UpdateFormComplete: ${messageFromAxiosError(e)}`);
  }
}
